<!-- eslint-disable @typescript-eslint/no-explicit-any -->
<template>
  <CheckoutLayout>
    <div>
      <!-- INFO: Status Bar -->
      <div class="mb-6">
        <div class="flex items-center justify-center text-base text-gray-600 mb-4">
          <span class="bg-primary text-primary-foreground rounded-full size-6 flex items-center justify-center">
            <IcBaselineCheck />
          </span>
          <div class="flex-1 h-px bg-primary mx-2"></div>
          <RouterLink
            :to="{ name: 'checkout-booking-details' }"
            class="bg-primary text-primary-foreground rounded-full size-6 flex items-center justify-center">
            <IcBaselineCheck />
          </RouterLink>
          <div class="flex-1 h-px bg-primary mx-2"></div>
          <span class="bg-primary text-white rounded-full size-6 flex items-center font-semibold justify-center"
            >3</span
          >
        </div>
        <h2 class="text-center text-xs font-semibold text-foreground">{{ $t("Payment and order confirmation") }}</h2>
      </div>

      <TicketsOverview class="py-4" />

      <hr />

      <!-- INFO: Form -->
      <form class="text-foreground pb-6" @submit.prevent="">
        <div class="grid xl:grid-cols-2 gap-4 lg:gap-8">
          <div class="space-y-4 lg:space-y-8">
            <!-- INFO: Display Payment methods only if there are tickets in the cart  -->
            <CheckoutPaymentMethods v-if="cartStore?.items?.length" />
          </div>

          <div class="space-y-4 lg:space-y-8">
            <CheckoutCard class="gap-3 lg:gap-6 h-fit">
              <h3 class="font-medium text-lg">{{ $t("Order overview") }}</h3>

              <!-- Cart overview + time left -->
              <!-- <CheckoutCartOverview /> -->
              <div>
                <CartReservation class="bg-muted text-muted-foreground px-6 py-4" />
                <ReservationTotals v-if="cartStore.items?.length" class="" />
              </div>

              <template v-if="cartStore.timeLeft">
                <CheckoutConsent />

                <!-- FIXME: Fix name: I think its not working currently on the production either -->
                <Checkbox
                  v-if="brandconfig.BRAND == 'homefans'"
                  name="booking.marketing_optin"
                  :label="
                    $t('By checking this box, you agree to be contacted') +
                    ' ' +
                    $t('via email by Homefans for commercial purposes')
                  " />

                <Checkbox
                  v-if="hasPackageRate"
                  name="packageRateConfirmed"
                  :label="
                    $t(
                      'I confirm that I have made a booking for an overnight stay, either independently or through a travel agency, in the vicinity of the event.'
                    )
                  " />
                <div
                  v-if="hasHungarianEvent && ['low', 'high'].includes(vatCategory || '')"
                  class="text-base px-2 flex items-center">
                  {{ $t("Invoice will be sent within 14 days due to local tax requirements.") }}
                </div>

                <div class="flex gap-3">
                  <Button
                    color="success"
                    class="w-full items-center gap-2 h-12"
                    data-testid="payment-button"
                    @click="submit">
                    {{ $t("Confirm order and pay") }}
                  </Button>
                </div>
              </template>
              <CheckoutUsps />
            </CheckoutCard>
          </div>
        </div>
      </form>
    </div>
    <component :is="render" />
  </CheckoutLayout>
</template>

<script setup lang="ts">
import IcBaselineCheck from "~icons/ic/baseline-check";
import CheckoutPaymentMethods from "../CheckoutPaymentMethods.vue";
import CheckoutConsent from "../CheckoutConsent.vue";
import { onBeforeRouteLeave, useRouter } from "vue-router";
import { useCartStore } from "@/store/cart.ts";
import { computed, onMounted, ref } from "vue";
import { useBookingsStore } from "@/store/bookings.js";
import { useUserStore } from "@/store/user.js";
import brandconfig from "@/brandconfig";
import CartReservation from "@/components/cart/CartReservation.vue";
import { useConfirm } from "@xs2event/js-lib/composables";
import { Button, Checkbox } from "@xs2event/js-lib/components";
import { useI18n } from "vue-i18n";
import { until } from "@vueuse/core";
import CheckoutLayout from "./CheckoutLayout.vue";
import ReservationTotals from "@/components/reservations/ReservationTotals.vue";

import { object, string, boolean, array, useForm, bool } from "@xs2event/js-lib/validation";
import type { InferType, ObjectSchema } from "@xs2event/js-lib/validation";
import { type BookingPost } from "@xs2event/js-lib/api";
import CheckoutCard from "../CheckoutCard.vue";
import { useMutation } from "@tanstack/vue-query";
import { ReservationsApi, ResponseError } from "@xs2event/js-lib/api";
import { useVatValidation } from "@xs2event/js-lib/composables";
import { snakeToCamel } from "@/helpers/casing";
import { useToast } from "@xs2event/js-lib/toast";
import { api } from "@/helpers/api";
import { getLocale } from "@/components/page/menu/locales.js";
import CheckoutUsps from "@/components/checkout/CheckoutUsps.vue";
import { formatDateOfBirth } from "@/helpers/format";
import TicketsOverview from "@/components/checkout/b2c/ticketSummary/TicketsOverview.vue";

const cartStore = useCartStore();
const userStore = useUserStore();
const bookingStore = useBookingsStore();
const { t } = useI18n();
const { addToast } = useToast();
const reservationsApi = new ReservationsApi();
const router = useRouter();

const { vatSchema } = useVatValidation();
const hasPackageRate = ref(cartStore.items.some((item) => item.ticket.local_rates.package_rate));
const hasHungarianEvent = computed(() => cartStore.items?.some((event) => event.event?.iso_country === "HUN"));
const vatCategory = computed(() => {
  if (!hasHungarianEvent.value) return undefined;

  const eventHung = cartStore.items.find((event) => event.event?.iso_country === "HUN");
  const ticket = cartStore.payDetails?.tickets?.find((ticket) => ticket.ticket_id == eventHung?.ticket?.ticket_id);
  return ticket?.vat_category;
});

const schema = computed(() =>
  object({
    marketing_optin: boolean(),
    confirmed: boolean().oneOf([true], t("Required")).required(), // Orders Details - TOC
    addPaymentAndOrBookingReferences: boolean(), // Order Confirmation - Payment Methods
    packageRateConfirmed: bool().test(
      "is-required-if-has-package-rate",
      t("Required"),
      (value) => (hasPackageRate.value ? value == true : true) // Order Confirmation
    ),
    email: string().email(t("string.email")), // Order Confirmation - Contact Details
    booking: object({
      invoiceReference: string().max(100),
      bookingReference: string().max(100),
      paymentMethod: string().required(),
      guests: array(),
      country: string(),
      ...vatSchema(t("string.invalidVat")),
    }).required() as ObjectSchema<BookingPost>,
  })
);

const { promptConfirm, render } = useConfirm({
  title: t("Leave page"),
  content: t("Are you sure you want to leave this page? All changed made will be gone."),
  onConfirm: () => {
    confirmed.value = true;
  },
});

const confirmed = ref(false);
onBeforeRouteLeave(async (to, from) => {
  const isLeavingCheckout = from.path.includes("/checkout") && !to.path.includes("/checkout");
  if (isLeavingCheckout && !confirmed.value) {
    promptConfirm();
    await until(confirmed).toBeTruthy();
    // Reset confirmed state for next time
    confirmed.value = false;
    return true; // Allow navigation to continue
  }
  return true; // Allow navigation if no confirmation needed
});

onMounted(() => {
  // Redirect to checkout booking details if no booking details are available
  if (!cartStore.bookingDetails || Object.keys(cartStore.bookingDetails).length === 0) {
    router.push({ name: "checkout-booking-details" });
    return;
  }

  if (router.currentRoute.value.query.paymentStatus && router.currentRoute.value.query.paymentStatus == "failed") {
    addToast({
      title: t("Payment failed"),
      content: t("Your payment could not be processed."),
      duration: 3000,
      status: "error",
    });
  }
});

const createPaymentMutation = useMutation({
  mutationFn: async (values: {
    amount: number;
    currency: string;
    reservation_id: string;
    distributor_id: string;
    client_id: string;
    payment_reference?: string;
    booking_reference?: string;
    payment_method: string;
    total_fixed_fee: number;
    total_fixed_fee_vat: number;
    payment_costs: number;
    number: number;
    booking_email: string;
    location: string;
  }) => {
    const response = await bookingStore.createMolliePayment(values);

    return response;
  },
});

type FormData = InferType<(typeof schema)["value"]>;

const form = useForm<FormData>({
  validationSchema: schema,
  initialValues: {
    ...{
      booking: {
        country: userStore?.distributor.iso_country,
      },
    },
  },
});

const isSubmitting = ref(false);
const submit = form.handleSubmit(async (values) => {
  // Prevent navigation warning
  confirmed.value = true;

  if (isSubmitting.value) return;

  // Checkout Step 1
  const bookingDetailsData = cartStore.bookingDetails;
  // Checkout Step 2
  const orderConfirmationData = values;

  isSubmitting.value = true;

  try {
    // B2C - Save contact details
    await saveContactDetails(bookingDetailsData.contactDetails);

    if (bookingDetailsData.guests && Object.keys(bookingDetailsData.guests).length) {
      await saveGuestData(bookingDetailsData.guests);
    }

    const paymentPayload = getPaymentPayload(bookingDetailsData, orderConfirmationData);
    const response = await createPaymentMutation.mutateAsync(paymentPayload);

    if (
      ["invoice", "pay_by_30days", "pay_by_60days", "pay_by_90days", "pay_by_180days"].includes(
        orderConfirmationData.booking.paymentMethod!
      )
    ) {
      // B2C - Notify booking
      bookingStore.bookingNotify(cartStore.reservationId);
      router.push({ path: `/checkout/payment-complete/${cartStore.reservationId}` });

      await new Promise((resolve) => setTimeout(resolve, 1000));
      isSubmitting.value = false;
    } else {
      location.replace(response.checkout);
    }
  } catch (error) {
    console.log(error);
    const isGuestDataError = (error as ResponseError)?.response?.status === 422;
    addToast({
      title: t("Payment failed"),
      content: isGuestDataError
        ? t("Invalid or missing guest data. Please check the guest details and try again.")
        : t("A server error occurred. Please try again."),
      status: "error",
      duration: 3000,
    });
    isSubmitting.value = false;
  }
});

// Helpers

const getPaymentPayload = (
  bookingDetailsData: Record<string, unknown>,
  orderConfirmationData: Record<string, unknown>
) => {
  return {
    amount: cartStore.payDetails?.total.sub_total,
    currency: "EUR",
    reservation_id: cartStore.reservationId,
    distributor_id: userStore.distributor_id,
    client_id: userStore.client_id,
    payment_reference: orderConfirmationData.booking.invoiceReference,
    booking_reference: orderConfirmationData.booking.bookingReference,
    payment_method: orderConfirmationData.booking.paymentMethod,
    total_fixed_fee: cartStore.payDetails?.ticket_fee?.sub_total,
    total_fixed_fee_vat: cartStore.payDetails?.vat?.sub_total,
    payment_costs: cartStore.payDetails?.payment_fee?.sub_total,
    number: cartStore.payDetails?.ticket_fee?.quantity,
    booking_email: orderConfirmationData?.email || bookingDetailsData?.email || userStore.username,
    location: location.host,
  };
};

const saveContactDetails = async (contactDetails: unknown) => {
  await api.post("/reservations/" + cartStore.reservationId + "/contact-details", {
    ...contactDetails,
    preferred_contact_language: getLocale(),
  });
};

const saveGuestData = async (guestData: { [key: string]: unknown }) => {
  await reservationsApi.reservationguestdataPost({
    reservationId: cartStore.reservationId,
    reservationItemsWithGuestsPost: {
      items: Object.entries(guestData).map(([key, entry]) => ({
        quantity: Object.keys(entry).length,
        ticketId: key,
        guests: Object.entries(entry).map(([, guest], index) => ({
          ...snakeToCamel({
            ...guest,
            dateOfBirth: guest.date_of_birth ? formatDateOfBirth(guest.date_of_birth) : undefined,
          }),
          leadGuest: index == 0,
        })),
      })),
    },
  });
};
</script>
